import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import React, { useState } from 'react';
import type { TooltipPayload } from 'recharts/types/state/tooltipSlice';
import type { BarChartDatum, BarDef, ChartTooltipRow } from 'shared/components/charts';
import { BarChartCard, ChartDetailModal, ChartTooltip } from 'shared/components/charts';
import {
  BILLABLE_COLOR,
  FALLBACK_PROJECT_COLOR,
  NON_BILLABLE_COLOR,
  formatBarLabel,
  formatBarLabelWithDay,
  generateDateKeys,
  resolveChartGranularity,
  secondsToHMS,
  secondsToHours,
} from 'shared/components/charts';
import { displayDuration } from 'shared/lib/formatDuration';

import {
  chartCardSx,
  chartMenuItemSx,
  chartModeDropdownSx,
  chartModeValueSx,
  chartModeViewBySx,
  ChartHeaderRight,
  ChartHeaderRow,
  ChartLegendDot,
  ChartLegendItem,
  ChartLegendRow,
  ChartOuterBox,
  ChartTitle,
} from '../../reports/reports.styles';
import type {
  DashboardByDateEntry,
  DashboardChartMode,
  DashboardProjectEntry,
} from '../types/dashboard.types';
import { DASHBOARD_CHART_MODES } from '../types/dashboard.types';

const BILLABILITY_BARS: BarDef[] = [
  { dataKey: 'billable', color: BILLABLE_COLOR, label: 'Billable' },
  { dataKey: 'nonBillable', color: NON_BILLABLE_COLOR, label: 'Non-billable' },
];

interface Props {
  from: string;
  to: string;
  byDate: DashboardByDateEntry[];
  byProject: DashboardProjectEntry[];
}

const DashboardBarChart: React.FC<Props> = ({ from, to, byDate, byProject }) => {
  const [mode, setMode] = useState<DashboardChartMode>('Billability');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [modalDatum, setModalDatum] = useState<BarChartDatum | null>(null);

  const granularity = resolveChartGranularity(from, to);
  const allKeys = generateDateKeys(from, to, granularity);

  const topProjects = byProject;
  const projectBars: BarDef[] = topProjects.map((p) => ({
    dataKey: p.projectId,
    label: p.name,
    color: p.color ?? FALLBACK_PROJECT_COLOR,
  }));

  const projectsTotalSecs = topProjects.reduce((s, p) => s + p.totalSecs, 0);

  const aggregatedByKey = new Map<string, { billableSecs: number; nonBillableSecs: number }>();
  byDate.forEach((entry) => {
    const bucketKey = granularity === 'month' ? entry.date.slice(0, 7) : entry.date;
    const existing = aggregatedByKey.get(bucketKey) ?? { billableSecs: 0, nonBillableSecs: 0 };
    aggregatedByKey.set(bucketKey, {
      billableSecs: existing.billableSecs + entry.billableSecs,
      nonBillableSecs: existing.nonBillableSecs + entry.nonBillableSecs,
    });
  });

  const chartData: BarChartDatum[] = allKeys.map((key) => {
    const agg = aggregatedByKey.get(key);
    const dateTotalSecs = agg ? agg.billableSecs + agg.nonBillableSecs : 0;

    if (mode === 'Billability') {
      return {
        key,
        label: formatBarLabel(key),
        billable: agg ? secondsToHours(agg.billableSecs) : 0,
        nonBillable: agg ? secondsToHours(agg.nonBillableSecs) : 0,
        _totalSecs: dateTotalSecs,
        _billableSecs: agg?.billableSecs ?? 0,
      };
    }

    const datum: BarChartDatum = {
      key,
      label: formatBarLabel(key),
      _totalSecs: dateTotalSecs,
    };
    topProjects.forEach((p) => {
      datum[p.projectId] =
        projectsTotalSecs > 0 && agg
          ? secondsToHours(Math.round((p.totalSecs / projectsTotalSecs) * dateTotalSecs))
          : 0;
    });
    return datum;
  });

  const buildTooltipRows = (
    datum: BarChartDatum,
    limit?: number,
  ): { rows: ChartTooltipRow[]; overflow?: number } => {
    const totalSecs = (datum._totalSecs as number) ?? 0;

    if (mode === 'Billability') {
      const bSecs = (datum._billableSecs as number) ?? 0;
      const nbSecs = totalSecs - bSecs;
      return {
        rows: [
          {
            color: BILLABLE_COLOR,
            label: 'Billable',
            duration: displayDuration(secondsToHMS(bSecs)),
            percentage: totalSecs > 0 ? `${Math.round((bSecs / totalSecs) * 100)}%` : undefined,
          },
          {
            color: NON_BILLABLE_COLOR,
            label: 'Non-billable',
            duration: displayDuration(secondsToHMS(nbSecs)),
            percentage: totalSecs > 0 ? `${Math.round((nbSecs / totalSecs) * 100)}%` : undefined,
          },
        ],
      };
    }

    const allRows: ChartTooltipRow[] = topProjects
      .map((p) => {
        const secs =
          projectsTotalSecs > 0 ? Math.round((p.totalSecs / projectsTotalSecs) * totalSecs) : 0;
        return {
          color: p.color ?? FALLBACK_PROJECT_COLOR,
          label: p.name,
          duration: displayDuration(secondsToHMS(secs)),
          percentage: totalSecs > 0 ? `${p.percentage}%` : undefined,
          _secs: secs,
        };
      })
      .filter((r) => r._secs > 0)
      .map(({ _secs: _, ...r }) => r);

    if (limit !== undefined && allRows.length > limit) {
      return { rows: allRows.slice(0, limit), overflow: allRows.length - limit };
    }
    return { rows: allRows };
  };

  const renderTooltip = (payload: TooltipPayload | undefined): React.ReactNode => {
    const datum = payload?.[0]?.payload as BarChartDatum | undefined;
    if (!datum) return null;
    const totalSecs = (datum._totalSecs as number) ?? 0;
    if (totalSecs === 0) return null;
    const { rows, overflow } = buildTooltipRows(datum, 5);
    return (
      <ChartTooltip
        title={formatBarLabelWithDay(datum.key as string)}
        total={displayDuration(secondsToHMS(totalSecs))}
        rows={rows}
        overflow={overflow}
      />
    );
  };

  const modalRows: ChartTooltipRow[] = modalDatum ? buildTooltipRows(modalDatum).rows : [];
  const modalTotalSecs = modalDatum ? ((modalDatum._totalSecs as number) ?? 0) : 0;
  const isProjectMode = mode === 'Project';
  const activeBars = isProjectMode ? projectBars : BILLABILITY_BARS;

  return (
    <ChartOuterBox>
      <Box sx={chartCardSx}>
        <ChartHeaderRow>
          <ChartTitle>Time tracked</ChartTitle>

          <ChartHeaderRight>
            <Button
              size="small"
              variant="outlined"
              endIcon={<ArrowDropDownIcon sx={{ fontSize: '14px !important' }} />}
              onClick={(e) => setMenuAnchor(e.currentTarget)}
              sx={chartModeDropdownSx}
            >
              <Box component="span" sx={chartModeViewBySx}>
                View by
              </Box>
              <Box component="span" sx={chartModeValueSx}>
                {mode}
              </Box>
            </Button>

            {!isProjectMode && (
              <ChartLegendRow>
                {BILLABILITY_BARS.map((bar) => (
                  <ChartLegendItem key={bar.dataKey}>
                    <ChartLegendDot dotcolor={bar.color} />
                    <Box component="span" sx={{ fontSize: 11, color: 'text.secondary' }}>
                      {bar.label}
                    </Box>
                  </ChartLegendItem>
                ))}
              </ChartLegendRow>
            )}
          </ChartHeaderRight>
        </ChartHeaderRow>

        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {DASHBOARD_CHART_MODES.map((option) => (
            <MenuItem
              key={option}
              selected={mode === option}
              onClick={() => {
                setMode(option);
                setMenuAnchor(null);
              }}
              sx={chartMenuItemSx}
            >
              {option}
            </MenuItem>
          ))}
        </Menu>

        <BarChartCard
          data={chartData}
          bars={activeBars}
          height={280}
          tooltipContent={renderTooltip}
          onBarClick={(datum) => setModalDatum(datum)}
        />
      </Box>

      {modalDatum && (
        <ChartDetailModal
          open={Boolean(modalDatum)}
          onClose={() => setModalDatum(null)}
          title={formatBarLabelWithDay(modalDatum.key as string)}
          total={displayDuration(secondsToHMS(modalTotalSecs))}
          rows={modalRows}
        />
      )}
    </ChartOuterBox>
  );
};

export default DashboardBarChart;
